<script setup lang="ts">
import { bemBuilder } from "@chatfood/core-utils";
import type { BasketRepoError } from "@chatfood/dbe-client/errors";
import { isBasketRepoError } from "@chatfood/dbe-client/errors";
import {
  AtomLink,
  AtomRadio,
  AtomText,
  AtomTextarea,
  SatButton,
  MolButtonMoney,
  OrgTipPicker,
  SatElasticPanel,
  SatLink,
  useToast,
  useDate,
  SatIconButton,
} from "@chatfood/design-system";
import { FetchError } from "ofetch";
import { computed, onMounted, ref } from "vue";
import { BasketClaims } from "@/components/basket-claims";
import { BasketEventName } from "@/components/basket-event-name";
import { BasketPax } from "@/components/basket-pax";
import { BasketSummary } from "@/components/basket-summary";
import { ChangeOrderingMode } from "@/components/change-ordering-mode";
import { GuestForm } from "@/components/guest-form";
import { CrossSellItems } from "@/components/cross-sell-items";
import { GuestPrice } from "@/components/guest-price";
import { I18nTranslate } from "@/components/i18n-translate";
import { useModalHubStore } from "@/components/modal-hub";
import { PackagingSelector } from "@/components/packaging-selector";
import { useBffErrors } from "~/composables/use-bff-errors";
import { useBusinessRulesValidation } from "~/composables/use-business-rules-validation";
import { useDomainLabel } from "~/composables/use-domain-label";
import { useLocale } from "~/composables/use-locale";
import { useNavigation } from "~/composables/use-navigation";
import type { IDomainMoney } from "~/domain/money";
import type { IDomainPaymentMethod } from "~/domain/payment-method";
import type { IDomainPickupPackaging } from "~/domain/pickup-packaging";
import { useBasketStore } from "~/stores/basket";
import { useBusinessStore } from "~/stores/business";
import { useCustomerStore } from "~/stores/customer";
import { useFeatureMatrixStore } from "~/stores/featureMatrix";
import { useGuestStore } from "~/stores/guest";
import { useOutletStore } from "~/stores/outlet";
import { useAnalytics, useBugReporter } from "#imports";
import { useHeaderHeight } from "~/composables/use-header-height";

const css = bemBuilder("checkout");
const { t } = useLocale();
const { toastError } = useToast();
const { report } = useBugReporter();
const { errorsToString } = useBffErrors();
const { push, resolvePath } = useNavigation();
const { formatTime, getReadableDate } = useDate();
const { paymentModeLabel } = useDomainLabel();
const { trackEvent } = useAnalytics();

const business = useBusinessStore();
const basket = useBasketStore();
const outlet = useOutletStore();
const customer = useCustomerStore();
const guest = useGuestStore();
const modalHub = useModalHubStore();
const useFeatureMatrix = useFeatureMatrixStore();

const notes = ref(basket.notes ?? "");

const selectedPaymentMethod = ref<IDomainPaymentMethod>();
const selectedTip = ref<IDomainMoney>();
const showCrossSell = ref(true);

const showChangeOrderingModeModal = ref(false);

onMounted(() => {
  outlet.refreshPaymentMethods(basket.orderingMode, basket.salesChannel);
  trackEvent({
    name: "initiateCheckout",
    payload: {
      total: basket.total,
      discountCode: basket.discountCode,
      items: basket.items.map((item) => ({
        id: item.itemId,
        name: item.name,
        quantity: item.quantity,
        price: item.unitPrice,
      })),
    },
  });

  if (outlet.orderingMode.paymentMethods.length === 1) {
    onPaymentSelect(outlet.orderingMode.paymentMethods[0].id);
  }
});

const trackTipSelection = (amount: IDomainMoney) => {
  trackEvent({
    name: "selectTipAmount",
    payload: {
      amount,
    },
  });
};

const onTipSelect = (tip: IDomainMoney) => {
  selectedTip.value = tip;
  trackTipSelection(tip);
};

const onNotesChange = (value: string) => {
  basket.setNotes((notes.value = value));
};

const onPaymentSelect = (id: string) => {
  const payment = outlet.orderingMode.paymentMethods.find(
    (method) => method.id === id,
  );

  if (selectedTip.value) {
    selectedTip.value = undefined;
  }

  if (payment) {
    selectedPaymentMethod.value = payment;

    trackEvent({
      name: "addPaymentInfo",
      payload: {
        method: payment.name,
      },
    });

    return;
  }

  toastError(t("pages.checkout.error_selecting_payment_message"), {
    title: t("pages.checkout.error_selecting_payment_title"),
  });

  report("Payment method not found", {
    context: {
      availablePayments: outlet.orderingMode.paymentMethods,
      selectedPaymentMethodId: id,
    },
  });
};

const customerAddress = computed(() => {
  return customer.addresses.find((address) => address.id === basket.addressId);
});

const deliveryAddress = computed(
  () => customerAddress.value?.location ?? guest.address?.location,
);

const deliveryEta = computed(() => {
  if (basket.scheduledTime) {
    return t("pages.checkout.scheduled_delivery", {
      date: getReadableDate(basket.scheduledTime.day, "ll"),
      time: formatTime(basket.scheduledTime.time),
    });
  }

  const eta = outlet.orderingMode.estimatedCompletionMinutes;

  return t("pages.checkout.eta_delivery", {
    from: eta,
    to: eta + 10,
  });
});

const pickupEta = computed(() => {
  if (basket.scheduledTime) {
    return t("pages.checkout.scheduled_pickup", {
      date: getReadableDate(basket.scheduledTime.day, "ll"),
      time: formatTime(basket.scheduledTime.time),
    });
  }

  const eta = outlet.orderingMode.preparationMinutes;

  return t("pages.checkout.eta_pickup", {
    from: eta,
    to: eta + 10,
  });
});

const { resolveBasketException } = useBusinessRulesValidation();

const toggleOrderingModeModal = () => {
  showChangeOrderingModeModal.value = !showChangeOrderingModeModal.value;
};

const resolveBasketRepoError = (error: BasketRepoError): boolean => {
  if (!error.hasMeta()) return false;

  if (error.isBusinessRule()) {
    const errorMessage =
      error.cause instanceof Error
        ? error.cause.message
        : t("pages.checkout.place_order_error_message");

    const violation = error.meta.violation;

    if (violation === "delivery_service_unavailable") {
      showChangeOrderingModeModal.value = true;
      return true;
    }

    modalHub.hideCheckout();

    const errorData = {
      statusCode: 422,
      statusMessage: violation,
      message: errorMessage,
      data: {
        item_id: error.meta.context.itemId,
      },
    };

    return resolveBasketException("checkout", errorData);
  }

  if (error.isValidation()) {
    const errorData = {
      statusCode: 422,
      data: {
        fields: error.meta,
      },
    };

    return resolveBasketException("checkout", errorData);
  }

  return false;
};

const resolveFetchError = (error: FetchError): boolean => {
  if (
    error.statusCode === 422 &&
    error.data.statusMessage === "delivery_service_unavailable"
  ) {
    showChangeOrderingModeModal.value = true;
    return true;
  }

  modalHub.hideCheckout();

  return resolveBasketException("checkout", error.data);
};

const processOrder = async (
  paymentMethod: IDomainPaymentMethod,
  tipAmount: number,
) => {
  const packagingType = outlet.orderingMode.acceptsPackaging
    ? selectedPackagingType.value
    : undefined;

  try {
    const response = await basket.checkout(
      paymentMethod,
      tipAmount,
      packagingType,
    );

    trackEvent({
      name: "placeOrder",
      payload: {
        orderId: response.orderId,
        orderUid: response.orderUid,
        total: basket.total,
        tax: basket.totalBreakdown.taxAmount,
        deliveryFee: basket.totalBreakdown.deliveryFee,
        tips: {
          value: tipAmount,
          currency: basket.total.currency,
        },
        discount: basket.totalBreakdown.discountedAmount,
        loyalty: basket.totalBreakdown.loyaltyRedeemed,
        serviceCharges: basket.totalBreakdown.serviceCharges,
        bagFee: basket.totalBreakdown.bagFee,
        discountCode: basket.discountCode,
        orderingMode: basket.orderingMode,
        salesChannel: basket.salesChannel,
        items: basket.items.map((item) => ({
          id: item.itemId,
          name: item.name,
          quantity: item.quantity,
          price: item.unitPrice,
        })),
        packagingType,
      },
    });

    if (response.paymentUrl) {
      modalHub.showLoading(
        t("pages.checkout.place_order_redirect_title"),
        t("pages.checkout.place_order_redirect_message"),
      );

      window.location.href = response.paymentUrl;
      return;
    }

    trackEvent({
      name: "orderSuccess",
      payload: {
        orderId: response.orderId,
        orderUid: response.orderUid,
      },
    });

    modalHub.hideCheckout();
    modalHub.hideLoading();

    await push("order-tracking", { trackingId: response.trackingId });

    basket.removeBasket();
  } catch (error: unknown) {
    modalHub.hideLoading();

    if (isBasketRepoError(error) && resolveBasketRepoError(error)) return;

    if (error instanceof FetchError && resolveFetchError(error)) return;

    const errorMessage =
      errorsToString(error, "\n") ??
      t("pages.checkout.place_order_error_message");

    modalHub.showError(
      t("pages.checkout.place_order_error_title"),
      errorMessage,
      t("pages.checkout.place_order_error_cta_label"),
    );
  }
};

const onCheckout = () => {
  if (!customer.isLogged && !guest.isPersonalInfoElegibleToCheckout) {
    return toastError(t("pages.checkout.error_guest_info"));
  }

  if (!selectedPaymentMethod.value) {
    return toastError(t("pages.checkout.error_payment_required"));
  }

  modalHub.showLoading(
    t("pages.checkout.place_order_loading_title"),
    t("pages.checkout.place_order_loading_message"),
  );

  const tipAmount = selectedTip.value?.value || 0;

  processOrder(selectedPaymentMethod.value, tipAmount);
};

const displayTcs = computed(() => business.termsAndConditionsLink);

const basketTotal = computed(() => {
  if (!selectedTip.value) return basket.totalBreakdown.outstandingBalance;

  return {
    currency: basket.total.currency,
    value:
      basket.totalBreakdown.outstandingBalance.value + selectedTip.value.value,
  };
});

const onEditAddress = () => {
  modalHub.hideCheckout();

  modalHub.showAddressForm(() => {
    modalHub.showCheckout();
  });
};

const backRoute = computed(() => {
  return resolvePath("outlet", {
    outletSlug: outlet.slug,
    orderingMode: basket.orderingMode,
  });
});

const { headerHeightPixel } = useHeaderHeight();

const selectedPackagingType = ref<IDomainPickupPackaging>("takeaway");
</script>

<template>
  <div :class="css('')">
    <div :class="css('header-desktop')">
      <AtomText color="color-text-headline" type="h2">
        {{ t("pages.checkout.header_title") }}
      </AtomText>
      <SatButton
        variant="primary"
        theme="ghost"
        size="medium"
        :route="backRoute"
      >
        {{ t("pages.checkout.back_cta") }}
      </SatButton>
    </div>
    <div :class="css('header-mobile')">
      <SatIconButton icon-name="arrow-left" theme="ghost" :route="backRoute">
        {{ t("pages.checkout.back_cta") }}
      </SatIconButton>
      <AtomText color="color-text-headline" type="h2">
        {{ t("pages.checkout.header_title") }}
      </AtomText>
      <div></div>
    </div>
    <div :class="css('overview')">
      <div :class="css('left-pane')">
        <div v-if="basket.isForDelivery" :class="css('section')">
          <AtomText color="color-text-headline" type="subtitle">
            {{ t("pages.checkout.delivery_headline") }}
          </AtomText>
          <AtomText
            v-if="!deliveryAddress"
            color="color-error"
            data-test="no-address"
          >
            {{ t("pages.checkout.missing_address") }}
          </AtomText>
          <template v-else>
            <div :class="css('address')" data-test="address-info">
              <div>
                <AtomText
                  v-if="customerAddress"
                  tag="div"
                  type="body-bold"
                  color="color-text-body"
                  data-test="address-nickname"
                >
                  {{ customerAddress.nickname }}
                </AtomText>
                <AtomText
                  tag="div"
                  color="color-text-secondary"
                  data-test="address-location"
                >
                  {{ deliveryAddress }}
                </AtomText>
              </div>
              <div>
                <AtomLink
                  to=""
                  data-test="edit-address"
                  @click.prevent="onEditAddress"
                >
                  {{ t("pages.checkout.edit_address_label") }}
                </AtomLink>
              </div>
            </div>
            <AtomText
              tag="div"
              data-test="deliver-eta"
              color="color-text-secondary"
            >
              {{ deliveryEta }}
            </AtomText>
          </template>
        </div>
        <div v-if="basket.isForPickup" :class="css('section')">
          <AtomText color="color-text-headline" type="subtitle">
            {{ t("pages.checkout.pickup_headline") }}
          </AtomText>
          <div>
            <AtomText
              tag="div"
              type="body-bold"
              color="color-text-body"
              data-test="outlet-name"
            >
              {{ outlet.name }}
            </AtomText>
            <AtomText
              tag="div"
              color="color-text-secondary"
              data-test="outlet-location"
            >
              {{ outlet.address.location }}
            </AtomText>
          </div>
          <AtomText
            tag="div"
            color="color-text-secondary"
            data-test="pickup-eta"
          >
            {{ pickupEta }}
          </AtomText>
        </div>
        <div v-if="!customer.isLogged" :class="css('section')">
          <GuestForm />
        </div>
        <div
          v-if="business.isCatering"
          data-test="event-guest"
          :class="[css('section'), css('meta-fields')]"
        >
          <BasketEventName auto-save />
          <BasketPax auto-save />
        </div>
        <div :class="css('section')">
          <AtomText color="color-text-headline" type="subtitle">
            {{ t("pages.checkout.payment_details") }}
          </AtomText>
          <AtomText
            v-if="!outlet.orderingMode.paymentMethods.length"
            color="color-error"
            data-test="no-payment-options"
          >
            {{ t("pages.checkout.missing_payment_methods") }}
          </AtomText>
          <template v-else>
            <AtomRadio
              v-for="payment in outlet.orderingMode.paymentMethods"
              :key="payment.id"
              name="payment-method"
              :value="payment.id"
              :checked="selectedPaymentMethod?.id === payment.id"
              :data-test="`payment-option-${payment.name}`"
              @change="onPaymentSelect"
            >
              {{ paymentModeLabel(payment.name) }}
            </AtomRadio>
          </template>
        </div>
        <Transition name="slide-fade">
          <div
            v-if="
              selectedPaymentMethod?.isOnline && business.config.isTipsEnabled
            "
            :class="css('section')"
          >
            <OrgTipPicker
              :title="t('pages.checkout.tip.title')"
              :subtitle="
                basket.isForDelivery && business.config.acceptsDriverTips
                  ? t('pages.checkout.tip.subtitle_driver')
                  : t('pages.checkout.tip.subtitle_restaurant')
              "
              :no-tip-button-label="t('pages.checkout.tip.no_tip')"
              :bill-amount="basket.totalBreakdown.basketTotal"
              :tip-percentages="business.config.tipPercentages"
              :allow-custom="true"
              :custom-amount-label="t('pages.checkout.tip.custom_amount')"
              :selected-amount="selectedTip?.value"
              @change="onTipSelect"
            />
          </div>
        </Transition>
        <div
          v-if="outlet.orderingMode.acceptsPackaging"
          :class="css('section')"
        >
          <PackagingSelector v-model="selectedPackagingType" />
        </div>
        <div :class="css('section')">
          <BasketClaims />
        </div>
        <div
          v-if="useFeatureMatrix.isToggleEnabled('collect-order-notes')"
          :class="{
            [css('section')]: true,
            [css('section', 'no-border')]: true,
          }"
        >
          <AtomText>
            {{
              basket.isForPickup
                ? t("pages.checkout.pickup_order_note_label")
                : t("pages.checkout.delivery_order_note_label")
            }}
          </AtomText>
          <AtomTextarea
            :placeholder="t('pages.checkout.placeholder_order_note')"
            :auto-grow="true"
            :rows="2"
            :value="notes"
            data-test="notes"
            @change="onNotesChange"
          />
        </div>
        <div :class="css('cta-mobile')">
          <MolButtonMoney
            v-if="basket.items.length"
            :hide-cents="false"
            :locale="business.locale"
            size="large"
            data-test="checkout-cta"
            :price="basketTotal"
            @click="onCheckout"
          >
            {{ t("pages.checkout.cta_label") }}
          </MolButtonMoney>
          <GuestPrice />
        </div>
      </div>
      <div :class="css('pane-divider')"></div>
      <div :class="[css('right-pane'), css('right-pane', 'self-scroll')]">
        <div>
          <AtomText :class="css('title')" type="h3">
            {{ t("pages.checkout.title") }}
          </AtomText>
        </div>
        <BasketSummary :class="css('summary')" :tip-amount="selectedTip" />
        <div v-if="showCrossSell" :class="css('cross-sell-box')">
          <AtomText :class="css('cross-sell-title')" type="body-bold">{{
            t("pages.checkout.cross_sell_title")
          }}</AtomText>
          <CrossSellItems @has-items="showCrossSell = $event" />
        </div>
        <div :class="css('cta')">
          <MolButtonMoney
            v-if="basket.items.length"
            :hide-cents="false"
            :locale="business.locale"
            size="large"
            data-test="checkout-cta"
            :price="basketTotal"
            @click="onCheckout"
          >
            {{ t("pages.checkout.cta_label") }}
          </MolButtonMoney>
          <GuestPrice />
        </div>
      </div>
    </div>
    <div
      :class="{
        [css('cta-wrapper')]: true,
        [css('cta-wrapper', 'with-tcs')]: displayTcs,
      }"
      data-test="cta-wrapper"
    >
      <AtomText
        v-if="displayTcs"
        color="color-text-secondary"
        type="small"
        data-test="guest-tcs"
      >
        <I18nTranslate keypath="pages.checkout.guest_tcs.sentence">
          <template #link>
            <SatLink
              :href="business.termsAndConditionsLink"
              target="_blank"
              type="small"
              underlined
            >
              {{ t("pages.checkout.guest_tcs.link") }}
            </SatLink>
          </template>
        </I18nTranslate>
      </AtomText>
    </div>
    <SatElasticPanel
      v-model="showChangeOrderingModeModal"
      show-close
      width-fit-content
      padding="none"
    >
      <ChangeOrderingMode
        :class="css('selection')"
        :title="t('pages.checkout.change_ordering_mode_title')"
        :text="t('pages.checkout.change_ordering_mode_text')"
        @close="toggleOrderingModeModal"
        @change="modalHub.hideCheckout"
      />
    </SatElasticPanel>
  </div>
</template>

<style lang="scss" scoped>
$header-height: v-bind(headerHeightPixel);
$content-padding: calc(var(--spacing-2xl) * 2);

.slide-fade-enter-active {
  transition: all 250ms ease-out;
}

.slide-fade-leave-active {
  transition: all 250ms ease-in;
}

.slide-fade-enter-from,
.slide-fade-leave-to {
  transform: translateY(20px);
  opacity: 0;
}

.checkout {
  position: relative;
  max-width: 1050px;
  width: 100%;
  margin: 0 auto;

  &__header-desktop {
    padding: var(--spacing-lg) var(--spacing-md) 0 var(--spacing-md);
    justify-content: space-between;
    display: none;

    @media (width > $breakpoint-sm) {
      display: flex;
    }
  }

  &__header-mobile {
    padding: var(--spacing-lg) var(--spacing-md) 0 var(--spacing-md);
    justify-content: space-between;
    display: flex;

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__overview {
    padding: var(--spacing-md) 0;
    display: flex;
    flex-direction: column-reverse;
    gap: var(--spacing-md);
    justify-content: center;
    position: relative;
  }

  &__title {
    margin-bottom: var(--spacing-sm);
  }

  &__section {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-sm);
    padding-block: var(--spacing-md);

    &:first-child {
      padding-top: 0;
    }

    &:last-child {
      padding-bottom: 0;
    }

    &:not(:last-child) {
      border-bottom: solid 1px var(--color-base-95);
    }

    &--no-border {
      border-bottom: none !important;
    }
  }

  &__meta-fields {
    @media (width >= $breakpoint-sm) {
      display: grid;
      flex-direction: row;
      grid-template-columns: 2fr 1fr;
    }
  }

  &__pane-divider {
    background-color: var(--color-base-95);
    height: 1px;
    width: 100%;
    display: block;
  }

  &__cta-wrapper {
    display: flex;
    flex-direction: column;
    padding: var(--spacing-md);
    position: sticky;
    bottom: 0;

    @media (width > $breakpoint-md) {
      flex-direction: row;
      justify-content: flex-end;
      align-items: center;
      position: static;
      padding: var(--spacing-lg);
    }

    &--with-tcs {
      gap: var(--spacing-md);

      @media (width > $breakpoint-md) {
        justify-content: space-between;
      }
    }
  }

  &__selection {
    max-width: 100%;

    @media (width > $breakpoint-sm) {
      width: 500px;
    }
  }

  &__address {
    display: flex;
    gap: var(--spacing-sm);
    align-items: flex-start;
    justify-content: space-between;
  }

  &__cta {
    display: flex;
    margin: 0 var(--spacing-md) var(--spacing-md) var(--spacing-md);
    gap: var(--spacing-xs);

    @media (width < $breakpoint-sm) {
      display: none;
    }
  }

  &__cta-mobile {
    position: sticky;
    bottom: 0;
    background-color: var(--color-base-100);
    padding: 20px 0;
    display: flex;
    gap: var(--spacing-xs);
    margin: 0;

    @media (width > $breakpoint-sm) {
      display: none;
    }
  }

  &__left-pane,
  &__right-pane {
    background-color: var(--color-base-100);
    border-radius: var(--radii-card-lg);
  }

  &__left-pane {
    gap: var(--spacing-md);
    display: flex;
    flex-direction: column;
    padding: var(--spacing-md);
    height: fit-content;
  }

  $content: "";

  &__summary {
    max-height: 66vh;
    overflow-y: scroll;
  }

  &__right-pane {
    gap: var(--spacing-md);
    display: flex;
    flex-direction: column;
    padding: var(--spacing-md) 0;
    justify-content: space-between;
    height: fit-content;

    @media (width > $breakpoint-sm) {
      position: sticky;
      top: 85px;
    }

    @media (width >= $breakpoint-lg) {
      &--self-scroll {
        $content: &;

        max-height: calc(100dvh - $header-height - $content-padding);
        overflow: auto;

        #{ $content } {
          flex-grow: 1;
          overflow-y: auto;
        }
      }
    }
  }

  &__summary,
  &__title {
    margin: 0 var(--spacing-md);
  }

  @media (width > $breakpoint-sm) {
    &__overview {
      flex-direction: row;
    }

    &__pane-divider {
      display: none;
    }

    &__left-pane {
      width: 58%;
    }

    &__right-pane {
      width: 42%;
    }
  }

  &__cross-sell-box {
    display: flex;
    justify-content: center;
    align-items: flex-start;
    width: 100%;
    gap: var(--spacing-sm);
    padding-top: var(--spacing-md);
    flex-direction: column;
  }

  &__cross-sell-title {
    padding: var(--spacing-md) var(--spacing-md) 0 var(--spacing-md);
    border-top: solid 1px var(--color-base-95);
    width: 100%;
  }
}
</style>
